import {
    ChangeDetectionStrategy,
    Component, DestroyRef,
    EventEmitter, inject,
    Input,
    OnChanges,
    OnInit,
    Output,
    QueryList,
    SimpleChanges,
    ViewChildren
} from '@angular/core';
import {FormControl} from '@angular/forms';
import {MatAutocompleteTrigger} from '@angular/material/autocomplete';
import {Observable} from 'rxjs';
import {filter } from 'rxjs/operators';
import * as R from 'ramda';
import * as RA from 'ramda-adjunct';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {LocationSet} from '@emm/core/models/location-set.model';
import {LocationSetType} from '@emm/core/models/location-set-type.model';
import {Venue} from '@emm/core/models/venue.model';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'emm-location-set-picker',
    styleUrls: ['./location-set-picker.component.scss'],
    templateUrl: './location-set-picker.component.html',
    standalone: false
})
export class LocationSetPickerComponent implements OnInit, OnChanges {

    private readonly destroyRef: DestroyRef = inject(DestroyRef);
    locationSetCtrl = new FormControl();
    venueCtrl = new FormControl();
    parentLocationSetCtrl = new FormControl();

    @Input() disabled: boolean;
    @Input() isLocationSetTypeParent: boolean;
    @Input() locationSets: LocationSet[];
    @Input() parentLabel: string;
    @Input() parentLocationSets: LocationSet[];
    @Input() selectedFeatureScope: string;
    @Input() selectedLocationSetType: LocationSetType;
    @Input() selectedLocationSet$: Observable<LocationSet>;
    @Input() selectedVenue$: Observable<Venue>;
    @Input() venues: Venue[];
    @Input() hideVenueSelector = false; // Optional: hide venue selector when true
    @Output() venueFilter = new EventEmitter<string>();
    @Output() venueSelected = new EventEmitter<Venue>();
    @Output() locationSetFilter = new EventEmitter<string>();
    @Output() locationSetSelected = new EventEmitter<LocationSet>();
    @Output() parentLocationSetSelected = new EventEmitter<LocationSet>();
    @Output() resetSelected = new EventEmitter();
    @Output() resetSelectedVenue = new EventEmitter();
    selectedLocationSet;
    selectedVenue;

    @ViewChildren(MatAutocompleteTrigger) autocompleteTriggers: QueryList<MatAutocompleteTrigger>;

    private awaitingPastedMatch = false;

    constructor() {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['locationSets'] || changes['parentLocationSets']) {
            this.selectSinglePastedMatch();
        }

        if (changes['disabled']) {
            if (this.disabled) {
                this.parentLocationSetCtrl.disable({emitEvent: false});
                this.locationSetCtrl.disable({emitEvent: false});
                this.venueCtrl.disable({emitEvent: false});
                return;
            }
            this.parentLocationSetCtrl.enable({emitEvent: false});
            this.locationSetCtrl.enable({emitEvent: false});
            this.venueCtrl.enable({emitEvent: false});
        }
    }


    ngOnInit(): void {
        this.parentLocationSetCtrl.valueChanges
            .pipe(filter(RA.isNotNil), takeUntilDestroyed(this.destroyRef))
            .subscribe((val: LocationSet) => {
                this.locationSetCtrl.setValue(null, {emitEvent: false});
                this.parentLocationSetSelected.emit(val);
            });

        this.locationSetCtrl.valueChanges
            .pipe(filter(RA.isNotNil), takeUntilDestroyed(this.destroyRef))
            .subscribe((val: LocationSet) => {
                typeof val === 'object'
                    ? this.locationSetSelected.emit(val)
                    : this.locationSetFilter.emit(val);
            });

        this.venueCtrl.valueChanges
            .pipe(filter(RA.isNotNil), takeUntilDestroyed(this.destroyRef))
            .subscribe((val: Venue) => {
                typeof val === 'object'
                    ? this.venueSelected.emit(val)
                    : this.venueFilter.emit(val);
            });
            this.selectedLocationSet$?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((set: LocationSet) => {
                this.selectedLocationSet = set;
                this.locationSetCtrl.setValue(set, {emitEvent: false});
            });

            if (this.venueCtrl) {
                this.selectedVenue$?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((venue: Venue) => {
                    this.selectedVenue = venue;
                    this.venueCtrl.setValue(venue, {emitEvent: false});
                });
            }
    }


    autoCompleteDisplay(locationSet: LocationSet): string {
        return locationSet && `${locationSet.tag} - ${locationSet.name}`;
    }


    locationSetPasted(): void {
        this.awaitingPastedMatch = true;
    }


    locationSetTyped(): void {
        this.awaitingPastedMatch = false;
    }


    // A paste is a complete tag or name, so a single match needs no confirmation.
    // Typed input is left alone because the narrowing is still in progress.
    private selectSinglePastedMatch(): void {
        if (!this.awaitingPastedMatch) {
            return;
        }

        const options = this.isLocationSetTypeParent ? this.parentLocationSets : this.locationSets;

        if (options?.length !== 1) {
            return;
        }

        this.awaitingPastedMatch = false;
        this.locationSetCtrl.setValue(options[0]);
        this.autocompleteTriggers?.forEach((trigger) => trigger.closePanel());
    }


    autoCompleteDisplayVenue(venue: Venue): string {
        if (venue && venue.name) {
            return venue && venue.name;
        } else {
            return venue && venue.venueId;
        }
    }


    resetSelectedLocationSet(): void {
        this.awaitingPastedMatch = false;
        this.resetSelected.emit();
        this.locationSetCtrl.setValue(null);
    }


    resetSelectedVenueId(): void {
        this.resetSelectedVenue.emit();
        this.venueCtrl.setValue(null);
    }


    get placeholderText(): string {
        if (R.isNil(this.selectedLocationSetType)) {
            return 'Location Set';
        }
        if (this.selectedLocationSetType.tag === 'LOCATION') {
            return 'Type location name or number for selections';
        }
        return this.isLocationSetTypeParent
            ? this.parentLabel
            : this.selectedLocationSetType.name;
    }

    get venuePlaceHolderText(): string {
        if (this.selectedLocationSetType.tag === 'LOCATION') {
            return 'Type venue name or number for selections';
        }
    }


    get placeholderTextParent(): string {
        return this.selectedLocationSetType.name;
    }
}
